import i18next from 'i18next'

// Result of one password-strength evaluation, normalised to the few fields the indicator
// component actually renders. Keeps zxcvbn-ts internals out of the consumer.
export interface PasswordStrengthResult {
    score: 0 | 1 | 2 | 3 | 4
    crackTimeDisplay: string
    warning: string
    suggestions: string[]
}

const NEUTRAL_RESULT: PasswordStrengthResult = {
    score: 0,
    crackTimeDisplay: '',
    warning: '',
    suggestions: [],
}

interface ZxcvbnInstance {
    check: (
        password: string,
        userInputs?: (string | number)[],
    ) => {
        score: 0 | 1 | 2 | 3 | 4
        crackTimes: { offlineSlowHashingXPerSecond: { display: string } }
        feedback: { warning: string | null; suggestions: string[] }
    }
}

// Cached after the first successful initialisation so each subsequent call is synchronous on the
// already-configured zxcvbn-ts instance.
let zxcvbnInstance: ZxcvbnInstance | null = null
// Language used for the cached translations; refreshed whenever the active i18next language
// changes between calls so users see localised feedback.
let configuredLanguage: string | null = null
// In-flight initialisation, so concurrent first calls share a single dynamic import.
let initPromise: Promise<void> | null = null

async function initialise(language: string): Promise<void> {
    const [core, common, langDe, langEn] = await Promise.all([
        import('@zxcvbn-ts/core'),
        import('@zxcvbn-ts/language-common'),
        import('@zxcvbn-ts/language-de'),
        import('@zxcvbn-ts/language-en'),
    ])

    const translations = language.startsWith('en') ? langEn.translations : langDe.translations

    zxcvbnInstance = new core.ZxcvbnFactory({
        dictionary: {
            ...common.dictionary,
            ...langDe.dictionary,
            ...langEn.dictionary,
        },
        graphs: common.adjacencyGraphs,
        translations,
    }) as ZxcvbnInstance
    configuredLanguage = language
}

async function ensureInitialised(language: string): Promise<void> {
    if (zxcvbnInstance && configuredLanguage === language) {
        return
    }
    if (!initPromise || configuredLanguage !== language) {
        initPromise = initialise(language).finally(() => {
            initPromise = null
        })
    }
    await initPromise
}

// Evaluates the password with zxcvbn-ts and returns a normalised view of the score, the human
// readable cracking-time string and the top feedback strings. Returns a neutral score-0 result if
// the dynamic import fails so the UI degrades gracefully (server remains authoritative on submit).
export async function evaluatePassword(
    password: string,
    userInputs: (string | number)[] = [],
): Promise<PasswordStrengthResult> {
    if (!password) {
        return NEUTRAL_RESULT
    }

    const language = i18next.language ?? 'de'

    try {
        await ensureInitialised(language)
        if (!zxcvbnInstance) {
            return NEUTRAL_RESULT
        }
        const result = zxcvbnInstance.check(password, userInputs)
        return {
            score: result.score,
            crackTimeDisplay: result.crackTimes.offlineSlowHashingXPerSecond.display,
            warning: result.feedback.warning ?? '',
            suggestions: result.feedback.suggestions,
        }
    } catch {
        return NEUTRAL_RESULT
    }
}

// Resets cached state. Exported solely for tests so each case starts from a clean slate.
export function resetPasswordStrengthForTests(): void {
    zxcvbnInstance = null
    configuredLanguage = null
    initPromise = null
}
